import { InputBase } from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import { MuiInputAdornment, NumberFormatCustom } from '../mui-text';
import { TextField } from '../text-field/index';

type CompProps = React.ComponentProps<typeof TextField>;

/** A text field that allows for input of decimal currency values.
 * Note it is an uncontrolled input in the @see NumberFormatCustom component   */
export const CurrencyInput: React.FC<
	{
		amount?: string;
		setAmount?: (a: string) => void;
		positiveOnly?: boolean;
	} & Pick<CompProps, 'sx' | 'size' | 'fullWidth' | 'label' | 'onBlur' | 'defaultValue' | 'placeholder' | 'helperText' | 'status'>
> = ({ amount, setAmount, size, label, sx, onBlur, defaultValue, placeholder, helperText, status, positiveOnly, ...rest }) => {
	return (
		<>
			<TextField
				label={label}
				fullWidth
				placeholder={placeholder}
				value={amount}
				InputLabelProps={!defaultValue && !amount && !!placeholder ? { shrink: true } : {}}
				defaultValue={defaultValue}
				size={size ?? undefined}
				sx={sx ?? undefined}
				helperText={helperText}
				onBlur={onBlur}
				onChange={e => {
					setAmount?.(e.target.value);
				}}
				status={status}
				InputProps={{
					inputComponent: NumberFormatCustom,
					inputProps: { positiveOnly }
				}}
				{...rest}
			/>
		</>
	);
};
const Dollar = (
	<MuiInputAdornment sx={{ height: 'auto' }} position="start">
		$
	</MuiInputAdornment>
);
export const CurrencyInputRaw = forwardRef<
	any,
	{
		// controlledUsage
		value?: string;
		// uncontrolled usage
		defaultValue?: string;
		label?: string;
		setAmount: (a: string) => void;
		size?: 'small' | 'medium';
		sx?: React.ComponentProps<typeof TextField>['sx'];
		onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
		onClick?: React.MouseEventHandler<HTMLInputElement>;
		autoFocus?: boolean;
	}
>(({ value, defaultValue, setAmount, size, onClick, autoFocus, sx, onBlur }, ref) => {
	return (
		<>
			<InputBase
				inputRef={ref}
				autoFocus={autoFocus}
				onBlur={onBlur}
				onClick={onClick}
				value={value}
				defaultValue={defaultValue}
				size={size ?? undefined}
				sx={sx ?? undefined}
				onChange={e => {
					setAmount(e.target.value);
				}}
				startAdornment={Dollar}
				inputComponent={NumberFormatCustom}
			/>
		</>
	);
});

export function digitsOnly(str: string) {
	return str.replace(/[^\d.]+/g, '');
}
export function digitsAndNegativeOnly(str: string) {
	return str.replace(/[^-\d.]+/g, '');
}

// TODO this should be explicit currency stuff
export function toCents(amountDollars: string | number) {
	return Math.round(Number(typeof amountDollars === 'string' ? digitsAndNegativeOnly(amountDollars) : amountDollars) * 100);
}

export function toCurrency(amountCents?: number) {
	if (!amountCents) return '';

	return String(amountCents / 100);
}

export const GridCurrencyInput = ({
	val,
	allowFractionalCurrency,
	currency,
	setAmount,
	style
}: {
	val: number;
	allowFractionalCurrency?: boolean;
	currency?: string;
	setAmount: (amount: number) => void;
	style?: React.CSSProperties;
}) => {
	const [mode, setMode] = useState(false);
	const [hover, setHover] = useState(false);

	useEffect(() => {
		if (!hover) return;

		const cancel = setTimeout(() => {
			setMode(true);
		}, 1000);
		return () => clearTimeout(cancel);
	}, [hover]);

	if (mode) {
		return (
			<GridCurrencyInputEditing
				ref={r => {
					if (r) {
						r.focus();
					}
				}}
				// onClick={e => e.target.select()}
				onBlur={() => setMode(false)}
				val={val}
				style={style}
				onChange={setAmount}
				allowFractionalCurrency={allowFractionalCurrency ?? false}
				currency={currency ?? 'AUD'}
			/>
		);
	}

	const numDigits = 2;
	const maxDigits = allowFractionalCurrency ? numDigits + 4 : numDigits;
	return (
		<div
			style={{
				padding: '0.5em',
				...style
			}}
			onClick={() => requestAnimationFrame(() => setMode(true))}
			onMouseOver={() => setHover(true)}
			onMouseOut={() => setHover(false)}
		>
			$ {formatter(Number(val), numDigits, maxDigits)}
		</div>
	);
};

const formatter = (val: number, minDigits: number, maxDigits: number) => {
	return val.toLocaleString(undefined, { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits });
};

export const GridCurrencyInputEditing = forwardRef<
	any,
	{
		val: number;
		currency: string;
		allowFractionalCurrency: boolean;
		onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
		onChange: (amount: number) => void;
		style?: React.CSSProperties;
		onClick?;
	}
>(({ val, allowFractionalCurrency, onChange, onBlur, style, onClick }, ref) => {
	//TODO: Future work, get the currency in here (move to @fox/financial)
	const numDigits = 2;
	const maxDigits = allowFractionalCurrency ? numDigits + 4 : numDigits;
	return (
		<CurrencyInputRaw
			ref={ref}
			defaultValue={formatter(Number(val), numDigits, maxDigits)}
			onClick={onClick}
			onBlur={onBlur}
			sx={{ paddingLeft: '0.5rem', ...style }}
			setAmount={e => onChange(Number(e))}
		/>
	);
});
